import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { ethosScript, fexScript, recruitingScript, SCRIPT_TYPES } from '../../data/scripts';
import { useWorkspaceMerge } from '../../contexts/WorkspaceMergeContext';
import parseScriptTokens from './parseScriptTokens';
import { FiRotateCcw, FiChevronRight, FiChevronLeft } from 'react-icons/fi';

function evaluateFormula(formula, data) {
  const resolved = formula.replace(/[a-zA-Z_]\w*/g, (key) => {
    const raw = data[key];
    if (raw == null || raw === '') return '0';
    const cleaned = String(raw).replace(/[$,]/g, '');
    const val = parseFloat(cleaned);
    return isNaN(val) ? '0' : String(val);
  });
  if (!/^[\d\s.+\-*/()]+$/.test(resolved)) return 0;
  try {
    const result = new Function(`return (${resolved})`)();
    return typeof result === 'number' && isFinite(result) ? result : 0;
  } catch {
    return 0;
  }
}

const RELATIONSHIP_OPTIONS_EN = ['Spouse', 'Child', 'Parent', 'Sibling', 'Other'];
const RELATIONSHIP_OPTIONS_ES = ['Cónyuge', 'Hijo/a', 'Padre/Madre', 'Hermano/a', 'Otro'];
const BENEFICIARY_TYPES_EN = ['Primary', 'Contingent'];
const BENEFICIARY_TYPES_ES = ['Primario', 'Contingente'];

function BeneficiaryListField({ intake_data, setIntakeField, setMergeField, language }) {
  const isEs = language === 'es';
  const relationshipOptions = isEs ? RELATIONSHIP_OPTIONS_ES : RELATIONSHIP_OPTIONS_EN;
  const beneficiaryTypes = isEs ? BENEFICIARY_TYPES_ES : BENEFICIARY_TYPES_EN;

  const beneficiaries = intake_data.beneficiaries || [
    { name: intake_data.beneficiary || '', relationship: '', percentage: 100, type: 'Primary' },
  ];

  const update = (newList) => {
    setIntakeField('beneficiaries', newList);
    if (newList.length > 0 && newList[0].name) {
      setMergeField('beneficiary', newList[0].name);
    }
  };

  const updateRow = (index, field, value) => {
    const next = beneficiaries.map((b, i) => (i === index ? { ...b, [field]: value } : b));
    update(next);
  };

  const addRow = () => {
    update([...beneficiaries, { name: '', relationship: '', percentage: 0, type: 'Contingent' }]);
  };

  const removeRow = (index) => {
    update(beneficiaries.filter((_, i) => i !== index));
  };

  const totalPct = beneficiaries.reduce((sum, b) => sum + (parseFloat(b.percentage) || 0), 0);
  const pctOk = totalPct === 100;

  const inputCls = 'px-2 py-1.5 text-sm border-0 border-b border-gray-300 rounded-none outline-none focus:border-[#00AAFF] transition-colors bg-transparent';

  return (
    <div className="my-2 space-y-2">
      {beneficiaries.map((b, i) => (
        <div key={i} className="flex items-center gap-2 flex-wrap">
          <input
            type="text"
            value={b.name}
            onChange={(e) => updateRow(i, 'name', e.target.value)}
            placeholder={isEs ? 'Nombre completo' : 'Full name'}
            className={`${inputCls} min-w-[130px] flex-1`}
          />
          <select
            value={b.relationship}
            onChange={(e) => updateRow(i, 'relationship', e.target.value)}
            className={`${inputCls} min-w-[100px]`}
          >
            <option value="">{isEs ? 'Relación' : 'Relation'}</option>
            {relationshipOptions.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <input
            type="number"
            value={b.percentage}
            onChange={(e) => updateRow(i, 'percentage', e.target.value)}
            onWheel={(e) => e.target.blur()}
            className={`${inputCls} w-[60px] text-center`}
            min={0}
            max={100}
          />
          <span className="text-[10px] text-gray-400">%</span>
          <select
            value={b.type}
            onChange={(e) => updateRow(i, 'type', e.target.value)}
            className={`${inputCls} min-w-[95px]`}
          >
            {beneficiaryTypes.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          {beneficiaries.length > 1 && (
            <button
              type="button"
              onClick={() => removeRow(i)}
              className="text-red-400 hover:text-red-600 text-sm font-bold px-1 transition-colors"
              title={isEs ? 'Eliminar' : 'Remove'}
            >
              ✕
            </button>
          )}
        </div>
      ))}
      <div className="flex items-center justify-between pt-1">
        <button
          type="button"
          onClick={addRow}
          className="text-xs font-semibold text-[#00AAFF] hover:text-[#0088cc] transition-colors"
        >
          {isEs ? '+ Agregar Beneficiario' : '+ Add Beneficiary'}
        </button>
        <span className={`text-[10px] font-semibold ${pctOk ? 'text-green-600' : 'text-red-500'}`}>
          Total: {totalPct}%
        </span>
      </div>
    </div>
  );
}

function InlineField({ block, intake_data, setIntakeField, mergeFields, setMergeField, language }) {
  const { key, label, fieldType, options, formula, placeholder, source } = block;
  const isEs = language === 'es';

  const isMerge = source === 'merge';
  const value = isMerge ? (mergeFields[key] || '') : (intake_data[key] || '');
  const onChange = isMerge
    ? (k, v) => setMergeField(k, v)
    : (k, v) => setIntakeField(k, v);

  if (fieldType === 'calculated') {
    const computed = evaluateFormula(formula, intake_data);
    return (
      <div className="flex items-center gap-3 py-1.5 px-3 my-1.5 rounded-md" style={{ background: '#f0f9ff' }}>
        <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 shrink-0">{label}</span>
        <span className="ml-auto text-sm font-bold tabular-nums" style={{ color: '#00AAFF' }}>
          ${computed.toLocaleString('en-US', { maximumFractionDigits: 0 })}
        </span>
      </div>
    );
  }

  if (fieldType === 'yesno') {
    const yesLabel = isEs ? 'Sí' : 'Yes';
    const noLabel = 'No';
    return (
      <div className="flex items-center gap-3 py-1.5 my-1">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 shrink-0">{label}</span>
        <div className="flex gap-1 ml-auto">
          {[{ display: yesLabel, val: 'Yes' }, { display: noLabel, val: 'No' }].map((opt) => (
            <button
              key={opt.val}
              type="button"
              onClick={() => onChange(key, opt.val)}
              className={`px-4 py-1 text-xs font-semibold rounded-full border transition-all ${
                value === opt.val
                  ? opt.val === 'Yes'
                    ? 'bg-green-500 text-white border-green-500'
                    : 'bg-red-500 text-white border-red-500'
                  : 'bg-white text-gray-500 border-gray-300 hover:border-gray-400'
              }`}
            >
              {opt.display}
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (fieldType === 'select') {
    return (
      <div className="flex items-center gap-3 py-1.5 my-1">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 shrink-0">{label}</span>
        <select
          value={value}
          onChange={(e) => onChange(key, e.target.value)}
          className="ml-auto px-2 py-1.5 text-sm bg-white border-0 border-b border-gray-300 rounded-none outline-none focus:border-[#00AAFF] transition-colors min-w-[140px]"
        >
          <option value="">{isEs ? '— Seleccionar —' : '— Select —'}</option>
          {(options || []).map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 py-1.5 my-1">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 shrink-0">{label}</span>
      <input
        type={fieldType === 'number' ? 'number' : 'text'}
        value={value}
        onChange={(e) => onChange(key, e.target.value)}
        onWheel={fieldType === 'number' ? (e) => e.target.blur() : undefined}
        placeholder={placeholder || ''}
        className="ml-auto px-2 py-1.5 text-sm border-0 border-b border-gray-300 rounded-none outline-none focus:border-[#00AAFF] transition-colors bg-transparent min-w-[140px]"
      />
    </div>
  );
}

function ContentBlockRenderer({ blocks, mergeFields, setMergeField, intake_data, setIntakeField, language }) {
  if (!blocks || blocks.length === 0) return null;

  return blocks.map((block, i) => {
    switch (block.type) {
      case 'script':
        return (
          <p key={i} className="text-[14px] leading-[1.75] text-gray-800 mb-3">
            {parseScriptTokens(block.text, mergeFields, setMergeField)}
          </p>
        );

      case 'stage':
        return (
          <p key={i} className="text-[13px] leading-[1.65] italic mb-3" style={{ color: '#94a3b8' }}>
            ({parseScriptTokens(block.text, mergeFields, setMergeField)})
          </p>
        );

      case 'field':
        return (
          <InlineField
            key={`field-${block.key}-${i}`}
            block={block}
            intake_data={intake_data}
            setIntakeField={setIntakeField}
            mergeFields={mergeFields}
            setMergeField={setMergeField}
            language={language}
          />
        );

      case 'beneficiaryList':
        return (
          <BeneficiaryListField
            key={`beneficiary-list-${i}`}
            intake_data={intake_data}
            setIntakeField={setIntakeField}
            setMergeField={setMergeField}
            language={language}
          />
        );

      case 'divider':
        return <hr key={i} className="my-4 border-gray-200" />;

      default:
        return null;
    }
  });
}

function getBlocks(step, language) {
  if (language === 'es') return step.contentBlocks_es || step.contentBlocks_en || step.contentBlocks || [];
  return step.contentBlocks_en || step.contentBlocks || [];
}

function getSection(step, language) {
  if (language === 'es') return step.section_es || step.section;
  return step.section;
}

function getBranchLabel(branch, language) {
  if (language === 'es') return branch.label_es || branch.label;
  return branch.label;
}

export default function ScriptPanel({ onBranchSelect, onScriptTypeChange, scriptType: controlledScriptType }) {
  const { fields, setMergeField, intake_data, setIntakeField, language, setLanguage } = useWorkspaceMerge();
  const [internalScriptType, setInternalScriptType] = useState('ethos');
  const scriptType = controlledScriptType ?? internalScriptType;
  const [history, setHistory] = useState([]);
  const scrollRef = useRef(null);

  const isEs = language === 'es';

  const isAdvancedPlaceholder = scriptType === 'advanced_planning';

  const script = useMemo(() => {
    if (scriptType === 'fex') return fexScript;
    if (scriptType === 'recruiting') return recruitingScript;
    if (scriptType === 'advanced_planning') return [];
    return ethosScript;
  }, [scriptType]);

  useEffect(() => {
    if (isAdvancedPlaceholder) {
      setHistory([]);
      return;
    }
    const first = script[0];
    if (first) {
      setHistory([{ stepId: first.id }]);
    }
  }, [script, isAdvancedPlaceholder]);

  const currentStepId = history.length > 0 ? history[history.length - 1].stepId : null;
  const currentStep = useMemo(
    () => script.find((s) => s.id === currentStepId) || null,
    [script, currentStepId],
  );
  const currentIndex = useMemo(
    () => script.findIndex((s) => s.id === currentStepId),
    [script, currentStepId],
  );

  const scrollToTop = useCallback(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const goToStep = useCallback(
    (stepId) => {
      setHistory((prev) => [...prev, { stepId }]);
      scrollToTop();
    },
    [scrollToTop],
  );

  const goBack = useCallback(() => {
    setHistory((prev) => {
      if (prev.length <= 1) return prev;
      return prev.slice(0, -1);
    });
    scrollToTop();
  }, [scrollToTop]);

  const restart = useCallback(() => {
    const first = script[0];
    if (first) {
      setHistory([{ stepId: first.id }]);
      scrollToTop();
    }
  }, [script, scrollToTop]);

  const handleScriptTypeChange = useCallback((type) => {
    if (controlledScriptType == null) setInternalScriptType(type);
    onScriptTypeChange?.(type);
  }, [controlledScriptType, onScriptTypeChange]);

  const handleNext = useCallback(() => {
    if (!currentStep) return;
    if (currentStep.nextStepId) {
      const target = script.find((s) => s.id === currentStep.nextStepId);
      if (target) goToStep(target.id);
    } else if (currentIndex >= 0 && currentIndex < script.length - 1) {
      const next = script[currentIndex + 1];
      goToStep(next.id);
    }
  }, [currentStep, currentIndex, script, goToStep]);

  const handleBranch = useCallback(
    (branch) => {
      goToStep(branch.nextStepId);
      onBranchSelect?.(branch.nextStepId, branch.label);
    },
    [goToStep, onBranchSelect],
  );

  const isLastStep =
    currentStep &&
    !currentStep.branches &&
    !currentStep.nextStepId &&
    currentIndex === script.length - 1;

  const toggleClass = (active) =>
    `px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
      active
        ? 'bg-primary text-white shadow-sm'
        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
    }`;

  const breadcrumbLabel = useCallback((h) => {
    const step = script.find((s) => s.id === h.stepId);
    return step ? getSection(step, language) : h.stepId;
  }, [script, language]);

  return (
    <div className="flex flex-col h-full">
      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="px-4 pt-4 pb-3 border-b border-gray-200 shrink-0 space-y-2.5">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">{isEs ? 'Guión de Llamada' : 'Call Script'}</h3>
          <div className="flex items-center gap-2">
            {(['ethos', 'fex', 'recruiting', 'advanced_planning']).map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => handleScriptTypeChange(type)}
                className={toggleClass(scriptType === type)}
              >
                {isEs ? SCRIPT_TYPES[type]?.label_es : SCRIPT_TYPES[type]?.label}
              </button>
            ))}

            {/* Language Toggle */}
            <div className="flex items-center ml-2 rounded-md overflow-hidden border border-gray-200">
              <button
                type="button"
                onClick={() => setLanguage('en')}
                className={`px-2.5 py-1.5 text-xs font-semibold transition-all ${
                  language === 'en'
                    ? 'bg-primary text-white'
                    : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                }`}
              >
                EN
              </button>
              <button
                type="button"
                onClick={() => setLanguage('es')}
                className={`px-2.5 py-1.5 text-xs font-semibold transition-all ${
                  language === 'es'
                    ? 'bg-primary text-white'
                    : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                }`}
              >
                ES
              </button>
            </div>

            <button
              type="button"
              onClick={restart}
              className="ml-2 flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
            >
              <FiRotateCcw className="h-3 w-3" />
              {isEs ? 'Reiniciar' : 'Restart'}
            </button>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="flex items-center gap-1 text-[11px] text-gray-400 flex-wrap leading-relaxed">
          {history.map((h, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span className="text-gray-300">→</span>}
              <span
                className={
                  i === history.length - 1
                    ? 'text-primary font-semibold'
                    : 'text-gray-400'
                }
              >
                {breadcrumbLabel(h)}
              </span>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* ── Script content ─────────────────────────────────────── */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5">
        {isAdvancedPlaceholder ? (
          <div className="rounded-lg border border-violet-200 bg-violet-50 p-6 text-center">
            <p className="text-sm font-semibold text-violet-900">
              {isEs
                ? 'Guión de Planificación Avanzada próximamente'
                : 'Advanced Planning script coming soon'}
            </p>
            <p className="text-xs text-violet-700 mt-2">
              {isEs
                ? 'Selecciona otro guión manualmente arriba mientras tanto.'
                : 'Select another script manually above in the meantime.'}
            </p>
          </div>
        ) : currentStep ? (
          <>
            <span className="inline-block text-[10px] font-bold uppercase tracking-widest text-primary bg-primary/10 px-2.5 py-1 rounded mb-4">
              {getSection(currentStep, language)}
            </span>
            <ContentBlockRenderer
              blocks={getBlocks(currentStep, language)}
              mergeFields={fields}
              setMergeField={setMergeField}
              intake_data={intake_data}
              setIntakeField={setIntakeField}
              language={language}
            />
          </>
        ) : null}
      </div>

      {/* ── Footer ─────────────────────────────────────────────── */}
      <div className="px-4 py-3 border-t border-gray-200 shrink-0 space-y-2">
        {!isAdvancedPlaceholder && currentStep?.branches ? (
          <>
            <div className="flex flex-wrap gap-2">
              {currentStep.branches.map((b) => (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => handleBranch(b)}
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-primary/10 text-primary hover:bg-primary hover:text-white transition-all border border-primary/20 hover:border-primary"
                >
                  {getBranchLabel(b, language)}
                </button>
              ))}
            </div>
            {history.length > 1 && (
              <button
                type="button"
                onClick={goBack}
                className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FiChevronLeft className="h-3 w-3" />
                {isEs ? 'Atrás' : 'Back'}
              </button>
            )}
          </>
        ) : !isAdvancedPlaceholder ? (
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={goBack}
              disabled={history.length <= 1}
              className="flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <FiChevronLeft className="h-4 w-4" />
              {isEs ? 'Atrás' : 'Back'}
            </button>
            {!isLastStep && (
              <button
                type="button"
                onClick={handleNext}
                className="flex items-center gap-1 px-4 py-2 text-sm font-semibold rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors shadow-sm"
              >
                {isEs ? 'Siguiente' : 'Next'}
                <FiChevronRight className="h-4 w-4" />
              </button>
            )}
            {isLastStep && (
              <span className="text-xs font-medium text-green-600 bg-green-50 px-3 py-1.5 rounded-md">
                {isEs ? '✅ Fin del Guión' : '✅ End of Script'}
              </span>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
